import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { EnterpriseBrandingEditor } from "@/components/platform/EnterpriseBrandingEditor";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  updateEnterprise,
  uploadEnterpriseLogo,
  uploadEnterpriseDarkLogo,
  enterpriseLogoUrl,
  enterpriseDarkLogoUrl,
  type Enterprise,
} from "@/services/platformEnterprisesService";

const ALLOWED_LOGO_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
const SLUG_RE = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;
const QUERY_KEY = ["platform", "enterprises"] as const;

interface FormValues {
  name: string;
  subdomain: string;
  primaryColor: string;
  primaryColorDark: string;
  isActive: boolean;
}

interface FormErrors {
  name?: string;
  subdomain?: string;
}

interface EnterpriseDetailsTabProps {
  enterprise: Enterprise;
  enterpriseId: string;
}

function formFromEnterprise(e: Enterprise): FormValues {
  return {
    name: e.name,
    subdomain: e.subdomain,
    primaryColor: e.primaryColor ?? "#000000",
    primaryColorDark: e.primaryColorDark ?? "",
    isActive: e.isActive,
  };
}

function validateForm(values: FormValues, t: (k: string) => string): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim()) {
    errors.name = t("platformEnterprises.validationRequired");
  }
  if (!values.subdomain.trim()) {
    errors.subdomain = t("platformEnterprises.validationRequired");
  } else if (!SLUG_RE.test(values.subdomain.trim())) {
    errors.subdomain = t("platformEnterprises.validationSlugFormat");
  }
  return errors;
}

export function EnterpriseDetailsTab({
  enterprise,
  enterpriseId,
}: EnterpriseDetailsTabProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormValues>(formFromEnterprise(enterprise));
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoLoadError, setLogoLoadError] = useState(false);

  const [darkLogoFile, setDarkLogoFile] = useState<File | null>(null);
  const [darkLogoPreview, setDarkLogoPreview] = useState<string | null>(null);
  const [darkLogoLoadError, setDarkLogoLoadError] = useState(false);

  useEffect(() => {
    setForm(formFromEnterprise(enterprise));
  }, [enterprise]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const result = await updateEnterprise(enterpriseId, {
        name: values.name.trim(),
        subdomain: values.subdomain.trim(),
        primaryColor: values.primaryColor || null,
        primaryColorDark: values.primaryColorDark.trim() || null,
        isActive: values.isActive,
      });
      if (logoFile) {
        try {
          await uploadEnterpriseLogo(enterpriseId, logoFile);
        } catch {
          toast.error(t("platformEnterprises.logoUploadError"));
        }
      }
      if (darkLogoFile) {
        try {
          await uploadEnterpriseDarkLogo(enterpriseId, darkLogoFile);
        } catch {
          toast.error(t("platformEnterprises.logoUploadError"));
        }
      }
      return result;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
        queryClient.invalidateQueries({
          queryKey: [...QUERY_KEY, "detail", enterpriseId],
        }),
      ]);
      setLogoFile(null);
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      setLogoPreview(null);
      setLogoLoadError(false);
      setDarkLogoFile(null);
      if (darkLogoPreview) URL.revokeObjectURL(darkLogoPreview);
      setDarkLogoPreview(null);
      setDarkLogoLoadError(false);
      toast.success(t("platformEnterprises.saveSuccess"));
    },
    onError: (err: unknown) => {
      const status =
        err != null &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        setServerError(t("platformEnterprises.errorDuplicateSubdomain"));
      } else {
        toast.error(t("platformEnterprises.saveError"));
      }
    },
  });

  const handleField = useCallback(
    (field: keyof Omit<FormValues, "isActive" | "primaryColorDark">) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        setServerError(null);
      },
    [],
  );

  const handleToggleActive = useCallback((checked: boolean): void => {
    setForm((prev) => ({ ...prev, isActive: checked }));
  }, []);

  const handleSubmit = useCallback((): void => {
    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setServerError(null);
    saveMutation.mutate(form);
  }, [form, saveMutation, t]);

  const isSaving = saveMutation.isPending;

  return (
    <div className="space-y-4">
      <Card className="shadow-sm">
        <CardContent className="space-y-5 p-6">
          {serverError != null && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/80 dark:bg-red-950/40 dark:text-red-300">
              {serverError}
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">
                {t("platformEnterprises.fieldName")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="edit-name"
                value={form.name}
                onChange={handleField("name")}
                disabled={isSaving}
              />
              {errors.name != null && (
                <p className="text-xs text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-subdomain">
                {t("platformEnterprises.fieldSubdomain")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="edit-subdomain"
                value={form.subdomain}
                onChange={handleField("subdomain")}
                disabled={isSaving}
                className="font-mono"
              />
              {errors.subdomain != null && (
                <p className="text-xs text-destructive">{errors.subdomain}</p>
              )}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("platformEnterprises.brandingSectionTitle")}</Label>
              <EnterpriseBrandingEditor
                enterpriseName={enterprise.name}
                disabled={isSaving}
                allowedLogoMimeTypes={ALLOWED_LOGO_TYPES}
                maxLogoBytes={MAX_LOGO_SIZE_BYTES}
                onSizeError={() => toast.error(t("platformEnterprises.logoSizeError"))}
                onFormatError={() => toast.error(t("platformEnterprises.logoFormatError"))}
                light={{
                  existingLogoSrc: enterprise.logoUrl
                    ? enterpriseLogoUrl(enterprise.id)
                    : null,
                  pendingLogoPreview: logoPreview,
                  logoLoadError,
                  color: form.primaryColor,
                  onPickFile: (file) => {
                    setLogoFile(file);
                    setLogoPreview(URL.createObjectURL(file));
                    setLogoLoadError(false);
                  },
                  onClearPendingFile: () => {
                    if (logoPreview) URL.revokeObjectURL(logoPreview);
                    setLogoFile(null);
                    setLogoPreview(null);
                  },
                  onColorChange: (hex) =>
                    setForm((prev) => ({ ...prev, primaryColor: hex })),
                  onLogoLoadError: () => setLogoLoadError(true),
                }}
                dark={{
                  existingLogoSrc: enterprise.logoUrlDark
                    ? enterpriseDarkLogoUrl(enterprise.id)
                    : null,
                  pendingLogoPreview: darkLogoPreview,
                  logoLoadError: darkLogoLoadError,
                  color: form.primaryColorDark,
                  colorEmpty: !form.primaryColorDark,
                  onPickFile: (file) => {
                    setDarkLogoFile(file);
                    setDarkLogoPreview(URL.createObjectURL(file));
                    setDarkLogoLoadError(false);
                  },
                  onClearPendingFile: () => {
                    if (darkLogoPreview) URL.revokeObjectURL(darkLogoPreview);
                    setDarkLogoFile(null);
                    setDarkLogoPreview(null);
                  },
                  onColorChange: (hex) =>
                    setForm((prev) => ({ ...prev, primaryColorDark: hex })),
                  onLogoLoadError: () => setDarkLogoLoadError(true),
                }}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-isActive">
                {t("platformEnterprises.fieldIsActive")}
              </Label>
              <div className="flex items-center gap-3 pt-1">
                <Switch
                  id="edit-isActive"
                  checked={form.isActive}
                  onCheckedChange={handleToggleActive}
                  disabled={isSaving}
                />
                <span className="text-sm text-muted-foreground">
                  {form.isActive
                    ? t("platformEnterprises.activeYes")
                    : t("platformEnterprises.activeNo")}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="flex flex-row flex-wrap items-center justify-between gap-3 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/platform/enterprises")}
            disabled={isSaving}
            className="transition-colors duration-150"
          >
            {t("pages.common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="transition-colors duration-150"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                {t("platformEnterprises.saving")}
              </>
            ) : (
              t("platformEnterprises.save")
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
